import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Facebook, Youtube, AtSign, MapPin, Phone, Mail, Clock, ExternalLink, HeartHandshake, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PublicHeader } from "@/components/PublicHeader";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";

export default function SocialPage() {
  const { t, lang } = useI18n();
  const [settings, setSettings] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (!cancelled) setSettings(data);
      } catch (_) { /* best-effort */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const socials = [
    { key: "instagram", icon: Instagram, label: "Instagram", url: settings.instagram, handle: "@iglesia_ncl", primary: true },
    { key: "facebook", icon: Facebook, label: "Facebook", url: settings.facebook },
    { key: "youtube", icon: Youtube, label: "YouTube", url: settings.youtube },
    { key: "tiktok", icon: AtSign, label: "TikTok", url: settings.tiktok },
    { key: "whatsapp", icon: Phone, label: "WhatsApp", url: settings.whatsapp },
  ].filter((s) => s.url);

  return (
    <div>
      <PublicHeader />
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="px-4 pt-4 space-y-4"
      >
        <div className="px-1">
          <div className="flex items-center gap-2">
            <AtSign className="h-5 w-5 text-primary" />
            <h1 className="font-serif-display text-2xl font-semibold tracking-tight">{t.social.title}</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{t.social.subtitle}</p>
        </div>

        {/* Meet Our Church card */}
        <Card className="overflow-hidden rounded-2xl shadow-sm ring-1 ring-black/5" data-testid="social-meet-our-church-card">
          <Link to="/team" className="block">
            <div
              className="px-5 py-4 flex items-center gap-3"
              style={{
                background:
                  "radial-gradient(600px circle at 100% 0%, rgba(200,30,58,0.14) 0%, transparent 55%), linear-gradient(180deg, hsl(var(--card)) 0%, hsl(var(--accent)) 100%)",
              }}
            >
              <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white ring-1 ring-black/5">
                <HeartHandshake className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-primary">
                  {lang === "es" ? "Nuestra iglesia" : "Our church"}
                </p>
                <p className="font-serif-display text-base font-semibold leading-tight">
                  {lang === "es" ? "Conoce Nuestra Iglesia" : "Meet Our Church"}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {lang === "es"
                    ? "Pastor, equipo de alabanza y concilio."
                    : "Pastor, worship team, and church council."}
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
            </div>
          </Link>
        </Card>

        {/* Instagram big card */}
        {settings.instagram && (
          <Card className="overflow-hidden rounded-2xl shadow-sm ring-1 ring-black/5">
            <div
              className="px-5 py-6"
              style={{
                background:
                  "radial-gradient(700px circle at 90% 10%, rgba(200,30,58,0.12) 0%, transparent 55%), linear-gradient(180deg, hsl(var(--accent)) 0%, hsl(var(--card)) 100%)",
              }}
            >
              <div className="flex items-center gap-3">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white ring-1 ring-black/5">
                  <Instagram className="h-6 w-6 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="font-serif-display text-lg font-semibold leading-tight">Instagram</p>
                  <p className="text-xs text-muted-foreground">@iglesia_ncl</p>
                </div>
              </div>
              <Button asChild className="mt-4 h-11 w-full" data-testid="social-instagram-cta">
                <a href={settings.instagram} target="_blank" rel="noreferrer">
                  {t.social.followInstagram}
                  <ExternalLink className="ml-1 h-4 w-4" />
                </a>
              </Button>
            </div>
          </Card>
        )}

        {/* Other socials */}
        {socials.filter((s) => !s.primary).length > 0 && (
          <div className="space-y-2" data-testid="social-other-list">
            {socials.filter((s) => !s.primary).map((s) => {
              const Icon = s.icon;
              return (
                <Card key={s.key} className="rounded-2xl ring-1 ring-black/5 shadow-sm">
                  <CardContent className="flex items-center gap-3 p-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{s.label}</p>
                      <p className="text-xs text-muted-foreground truncate">{s.url}</p>
                    </div>
                    <Button asChild size="sm" variant="outline" className="rounded-full">
                      <a href={s.url} target="_blank" rel="noreferrer">{t.social.visit}</a>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Contact / Info */}
        <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <p className="font-serif-display text-base font-semibold">{t.social.contact}</p>
            </div>
            {settings.address && (
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="h-4 w-4 text-primary mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{t.social.address}</p>
                  <p>{settings.address}</p>
                </div>
              </div>
            )}
            {settings.phone && (
              <div className="flex items-start gap-2 text-sm">
                <Phone className="h-4 w-4 text-primary mt-0.5" />
                <a className="underline-offset-2 hover:underline" href={`tel:${settings.phone}`}>{settings.phone}</a>
              </div>
            )}
            {settings.email && (
              <div className="flex items-start gap-2 text-sm">
                <Mail className="h-4 w-4 text-primary mt-0.5" />
                <a className="underline-offset-2 hover:underline" href={`mailto:${settings.email}`}>{settings.email}</a>
              </div>
            )}
            {(lang === "es" ? settings.service_times_es : settings.service_times_en) && (
              <div className="flex items-start gap-2 text-sm">
                <Clock className="h-4 w-4 text-primary mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{t.social.services}</p>
                  <p className="whitespace-pre-line">{lang === "es" ? settings.service_times_es : settings.service_times_en}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        <div className="h-2" />
      </motion.div>
    </div>
  );
}
